/**
 * @file     LeaseholdBadge.tsx
 * @purpose  Neutral leasehold info row with strata inquiry CTA.
 *           Shown above property details when LandLeaseYN is true.
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useLeadCapture, type ListingContext } from '@/components/lead-capture/LeadCaptureContext';

interface Props {
  listing: ListingContext;
}

export function LeaseholdBadge({ listing }: Props) {
  const { mode } = useAppTheme();
  const { open } = useLeadCapture();
  const isDark = mode === 'dark';

  return (
    <Box sx={{
      mt: 2,
      px: 1.5,
      py: 1,
      borderRadius: `${layoutTokens.radius.lg}px`,
      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}>
      <Box>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.35 }}>
          Land ownership
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mt: 0.2 }}>
          Leasehold (prepaid strata)
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        onClick={() => open({ mode: 'inquiry', listing })}
        sx={{ flexShrink: 0, fontSize: '0.72rem', fontWeight: 500, borderRadius: `${layoutTokens.radius.md}px`, px: 1.5, py: 0.5, whiteSpace: 'nowrap' }}
      >
        Ask about strata
      </Button>
    </Box>
  );
}
